import {
  BeneficiaryLegalStatus,
  BeneficiaryLegalStatusTranslationKey
} from "@/modules/transaction/common/enum";
import type { IGroupTransferBeneficiary, } from "@/modules/transaction/common/types";
import { Amount, BackButton } from "@/shared/components";
import { Copy } from "@ab.uitools/ui-library/components/Copy";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Text } from "@ab.uitools/ui-library/components/Text";
import type { RefObject } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  beneficiaries: IGroupTransferBeneficiary[];
  currency: string;
  onBack: () => void;
  scrollbarContainerRef?: RefObject<HTMLDivElement | null>;
}

export const BeneficiaryList = ({
  beneficiaries,
  currency,
  onBack,
  scrollbarContainerRef,
}: Props) => {
  const { t } = useTranslation();
  return (
    <div>
      <BackButton
        size="medium"
        className="my-12"
        buttonText="general.back"
        action={onBack}
      />
      {beneficiaries?.map((beneficiary) => (
        <div key={beneficiary.id} className="beneficiary-item-wrapper mb-32">
          <div>
            <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
              <Text
                className="text-nowrap truncate pr-16 max-w-50"
                type="secondary"
                size="medium"
                weight="semibold"
              >
                {t("transfer.group.beneficiaryPayeeName")}
              </Text>
              <Text
                className="text-nowrap truncate"
                size="medium"
                weight="bold"
              >
                {beneficiary.receiverName}
              </Text>
            </div>
            <Divider
              color="light"
              type="primary"
              isHorizontal
            />
          </div>
          <div>
            <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
              <Text
                className="text-nowrap"
                type="secondary"
                size="medium"
                weight="semibold"
              >
                {t("beneficiary.account")}
              </Text>
              <div className="flexbox align-items--center">
                <Copy
                  textAfterCopy={t("general.copied")}
                  size="medium"
                  parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                  text={beneficiary.receiverAccount as string}
                />
                <Text size="medium" weight="bold">
                  {beneficiary.receiverAccount}
                </Text>
              </div>
            </div>
            <Divider
              color="light"
              type="primary"
              isHorizontal
            />
          </div>
          {!!beneficiary?.beneficiaryLegalStatus &&
            beneficiary?.beneficiaryLegalStatus !== BeneficiaryLegalStatus.NotSet && (
              <div>
                <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                  <Text
                    className="text-nowrap"
                    type="secondary"
                    size="medium"
                    weight="semibold"
                  >
                    {t("transfer.beneficiaryLegalStatus")}
                  </Text>
                  <Text
                    className="truncate cursor-pointer pl-16"
                    size="medium"
                    weight="bold"
                  >
                    {
                      t(`general.${BeneficiaryLegalStatusTranslationKey[
                        beneficiary?.beneficiaryLegalStatus as keyof typeof BeneficiaryLegalStatusTranslationKey
                        ]}`
                      )
                    }
                  </Text>
                </div>
                <Divider
                  color="light"
                  type="primary"
                  isHorizontal
                />
              </div>
            )}
          <div>
            <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
              <Text
                className="text-nowrap"
                type="secondary"
                size="medium"
                weight="semibold"
              >
                {t("general.amount")}
              </Text>
              <Amount
                currency={currency}
                amount={beneficiary.amount as number}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
